import * as THREE from 'three'
import { InvalidGltfManifestError, MeshLoadCancelledError } from '@/plugins/mesh/errors'
import {
  getGltfComponentByteSize,
  getGltfComponentCount,
} from '@/plugins/mesh/loaders/gltfLowMemory/accessorLayout'
import { scanGltfCompatibility } from '@/plugins/mesh/loaders/gltfLowMemory/compatibility'
import type { GltfSceneAnalyzer } from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import type {
  GltfAccessor,
  GltfManifest,
  GltfManifestDocument,
  GltfPrimitiveSource,
  GltfSceneAnalysis,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import type { MeshRenderClass } from '@/plugins/mesh/types'

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function createLocalMatrix(
  manifest: GltfManifest,
  nodeIndex: number,
  source: string
): THREE.Matrix4 {
  const node = manifest.nodes?.[nodeIndex]
  if (!node) throw new InvalidGltfManifestError(source, `nodes[${nodeIndex}] 不存在`)
  if (node.matrix) return new THREE.Matrix4().fromArray(node.matrix)

  const translation = new THREE.Vector3().fromArray(node.translation ?? [0, 0, 0])
  const rotation = new THREE.Quaternion().fromArray(node.rotation ?? [0, 0, 0, 1])
  const scale = new THREE.Vector3().fromArray(node.scale ?? [1, 1, 1])
  return new THREE.Matrix4().compose(translation, rotation, scale)
}

function getRenderClass(
  manifest: GltfManifest,
  materialIndex: number | undefined
): MeshRenderClass {
  const alphaMode =
    materialIndex === undefined ? 'OPAQUE' : manifest.materials?.[materialIndex]?.alphaMode
  if (alphaMode === 'BLEND') return 'blend'
  if (alphaMode === 'MASK') return 'mask'
  return 'opaque'
}

function getAccessorLayoutKey(manifest: GltfManifest, accessorIndex: number): string {
  const accessor = manifest.accessors?.[accessorIndex]
  if (!accessor) return 'missing'
  const bufferView =
    accessor.bufferView === undefined ? undefined : manifest.bufferViews?.[accessor.bufferView]
  return [
    accessor.componentType,
    accessor.type,
    accessor.normalized ? 1 : 0,
    bufferView?.byteStride ?? 0,
  ].join(':')
}

function buildVertexLayoutKey(
  manifest: GltfManifest,
  attributes: Readonly<Record<string, number>>,
  indexAccessor: number | undefined
): string {
  const attributeKey = Object.entries(attributes)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(
      ([semantic, accessorIndex]) => `${semantic}:${getAccessorLayoutKey(manifest, accessorIndex)}`
    )
    .join('|')
  const indexKey =
    indexAccessor === undefined ? 'non-indexed' : getAccessorLayoutKey(manifest, indexAccessor)
  return `${attributeKey}|index:${indexKey}`
}

function createWorldBounds(
  accessor: GltfAccessor | undefined,
  worldMatrix: THREE.Matrix4
): THREE.Box3 | undefined {
  if (
    !accessor ||
    accessor.type !== 'VEC3' ||
    accessor.min?.length !== 3 ||
    accessor.max?.length !== 3 ||
    ![...accessor.min, ...accessor.max].every(Number.isFinite)
  ) {
    return undefined
  }
  return new THREE.Box3(
    new THREE.Vector3().fromArray(accessor.min),
    new THREE.Vector3().fromArray(accessor.max)
  ).applyMatrix4(worldMatrix)
}

function getPrimitiveCounts(
  manifest: GltfManifest,
  attributes: Readonly<Record<string, number>>,
  indexAccessor: number | undefined
): { vertexCount: number; indexCount: number } {
  const positionAccessor = manifest.accessors?.[attributes.POSITION]
  const vertexCount = positionAccessor?.count ?? 0
  return {
    vertexCount,
    indexCount:
      indexAccessor === undefined ? vertexCount : (manifest.accessors?.[indexAccessor]?.count ?? 0),
  }
}

/** 计算 accessor 在紧密最终数组中的字节数，供计划键和预算复用。 */
export function getAccessorTightByteLength(accessor: GltfAccessor): number {
  return (
    accessor.count *
    getGltfComponentCount(accessor.type) *
    getGltfComponentByteSize(accessor.componentType)
  )
}

/** 默认的静态 scene/node 分析器，不读取 accessor 数据。 */
export class DefaultGltfSceneAnalyzer implements GltfSceneAnalyzer {
  analyze(document: GltfManifestDocument, signal?: AbortSignal): GltfSceneAnalysis {
    const { manifest, source } = document
    const scenes = manifest.scenes ?? []
    const sceneIndex = manifest.scene ?? 0
    const scene = scenes[sceneIndex]
    if (!scene) {
      throw new InvalidGltfManifestError(source, `选定 scene 不存在: ${sceneIndex}`)
    }

    const primitives: GltfPrimitiveSource[] = []
    const visit = (nodeIndex: number, parentWorld: THREE.Matrix4): void => {
      throwIfCancelled(signal, source)
      const node = manifest.nodes?.[nodeIndex]
      if (!node) throw new InvalidGltfManifestError(source, `nodes[${nodeIndex}] 不存在`)
      const worldMatrix = parentWorld
        .clone()
        .multiply(createLocalMatrix(manifest, nodeIndex, source))
      if (node.mesh !== undefined) {
        const mesh = manifest.meshes?.[node.mesh]
        if (!mesh) throw new InvalidGltfManifestError(source, `meshes[${node.mesh}] 不存在`)
        mesh.primitives.forEach((primitive, primitiveIndex) => {
          const positionAccessor = manifest.accessors?.[primitive.attributes.POSITION]
          const worldBounds = createWorldBounds(positionAccessor, worldMatrix)
          const renderClass = getRenderClass(manifest, primitive.material)
          const counts = getPrimitiveCounts(manifest, primitive.attributes, primitive.indices)
          primitives.push({
            id: `node:${nodeIndex}/mesh:${node.mesh}/primitive:${primitiveIndex}`,
            nodeIndex,
            meshIndex: node.mesh!,
            primitiveIndex,
            attributeAccessors: primitive.attributes,
            indexAccessor: primitive.indices,
            materialIndex: primitive.material,
            nodeName: node.name ?? `node-${nodeIndex}`,
            worldMatrix: worldMatrix.clone(),
            worldBounds,
            spatialMergeSafe: worldBounds !== undefined,
            negativeDeterminant: worldMatrix.determinant() < 0,
            renderClass,
            vertexLayoutKey: buildVertexLayoutKey(
              manifest,
              primitive.attributes,
              primitive.indices
            ),
            materialKey:
              primitive.material === undefined ? 'default' : `material:${primitive.material}`,
            objectStateKey: 'visible:1|cast:0|receive:0|frustum:1|order:0|layers:1',
            shaderSemanticKey: `pbr:${renderClass}`,
            vertexCount: counts.vertexCount,
            indexCount: counts.indexCount,
          })
        })
      }
      for (const child of node.children ?? []) visit(child, worldMatrix)
    }

    for (const nodeIndex of scene.nodes ?? []) visit(nodeIndex, new THREE.Matrix4())
    return {
      sceneIndex,
      primitives,
      compatibilityIssues: scanGltfCompatibility(manifest),
    }
  }
}
